import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Phase H4 FE-1 — read-only branches list for catalogue scoping.
 *
 * The IA needs a branch picker on the Sections catalogue and the
 * Eligibility matrix. Branches are slow-moving (set up once at tenant
 * onboarding, occasionally added) so we keep them fresh for 5 minutes
 * and fetch up to 200 — well above the largest realistic K-12 chain
 * (multi-city groups cap at ~50 branches in our data set).
 *
 * The endpoint requires `manage_settings` server-side; users without
 * it get an empty list rather than a 403 (the BE handles the gate),
 * so this hook stays simple and any RBAC fall-back lives in the
 * caller.
 */

export interface BranchSummary {
  id: string;
  name: string;
  slug: string;
  code: string | null;
  is_flagship: boolean;
  status: string;
}

interface BranchListResponse {
  items: BranchSummary[];
  total: number;
}

const BRANCHES_KEY = ["branches", "list"] as const;

export function useBranches(opts?: { status?: string }) {
  const filterStatus = opts?.status ?? null;
  return useQuery<BranchListResponse>({
    queryKey: [...BRANCHES_KEY, { status: filterStatus }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      params.set("limit", "200");
      return api.get<BranchListResponse>(`/api/branches/?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}
